import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DataTable,
  FormField,
  Input,
  LoadingSkeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
  formatDateTime,
  toast,
  type DataTableColumn,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, BarChart3, Eye, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  type HomepageNews,
  type HomepageNewsBlock,
  type HomepageNewsBlockType,
  type HomepageNewsDraft,
  type HomepageNewsTone,
  useNotificationsApi,
} from './api'
import { HomepageNewsContent, HomepageNewsDialog } from './homepage-news-presentation'
import { HomepageNewsStatisticsDialog } from './homepage-news-statistics-dialog'

const EMPTY_BLOCK: HomepageNewsBlock = { type: 'paragraph', text: '', items: [] }

export function HomepageNewsSection() {
  const api = useNotificationsApi()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<HomepageNews | null>(null)
  const [form, setForm] = useState<NewsForm>(() => emptyForm())
  const [previewOpen, setPreviewOpen] = useState(false)
  const [cancelCandidate, setCancelCandidate] = useState<HomepageNews | null>(null)
  const [statisticsNews, setStatisticsNews] = useState<HomepageNews | null>(null)
  const [statusReferenceTime] = useState(() => Date.now())
  const news = useQuery({
    queryKey: ['notifications', 'homepage-news'],
    queryFn: ({ signal }) => api.get<HomepageNews[]>('/homepage-news', { signal }),
  })
  const save = useMutation({
    mutationFn: (draft: HomepageNewsDraft) =>
      editing
        ? api.put<HomepageNews>(`/homepage-news/${editing.id}`, draft)
        : api.post<HomepageNews>('/homepage-news', draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['notifications', 'homepage-news'],
      })
      toast.success(
        editing ? 'Startseiten-News aktualisiert' : 'Startseiten-News geplant',
      )
      setEditing(null)
      setForm(emptyForm())
    },
    onError: () => toast.error('Die Startseiten-News konnte nicht gespeichert werden.'),
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api.delete(`/homepage-news/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['notifications', 'homepage-news'],
      })
      setCancelCandidate(null)
      toast.success('Startseiten-News beendet')
    },
  })
  const draft = useMemo(() => toDraft(form), [form])
  const validation = validate(form)

  const columns: DataTableColumn<HomepageNews>[] = [
    {
      key: 'title',
      header: 'News',
      cell: (row) => (
        <div>
          <p className="font-medium">{row.title}</p>
          {row.eyebrow && <p className="text-xs text-muted-foreground">{row.eyebrow}</p>}
        </div>
      ),
    },
    {
      key: 'period',
      header: 'Zeitraum',
      cell: (row) => (
        <span className="text-sm">
          {formatDateTime(row.validFrom)} bis {formatDateTime(row.validUntil)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <NewsStatus news={row} now={statusReferenceTime} />,
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label={`${row.title} Nutzerstatistik anzeigen`}
            onClick={() => setStatisticsNews(row)}
          >
            <BarChart3 aria-hidden="true" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`${row.title} bearbeiten`}
            disabled={row.isCancelled}
            onClick={() => {
              setEditing(row)
              setForm(fromNews(row))
            }}
          >
            <Pencil aria-hidden="true" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`${row.title} beenden`}
            disabled={row.isCancelled}
            onClick={() => setCancelCandidate(row)}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      ),
    },
  ]

  if (news.isPending) return <LoadingSkeleton variant="card" rows={4} />

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {editing ? 'Startseiten-News bearbeiten' : 'Neue Startseiten-News'}
          </CardTitle>
          <CardDescription>
            Die Vorschau verwendet exakt dieselbe Darstellung wie das Popup nach der
            Anmeldung.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)]">
            <form
              className="space-y-5"
              onSubmit={(event) => {
                event.preventDefault()
                if (!validation) save.mutate(draft)
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  label="Kleine Überschrift"
                  htmlFor="homepage-news-eyebrow"
                  description="Zum Beispiel: Berky Digital"
                >
                  <Input
                    id="homepage-news-eyebrow"
                    maxLength={60}
                    value={form.eyebrow}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, eyebrow: event.target.value }))
                    }
                  />
                </FormField>
                <FormField label="Erscheinungsart" htmlFor="homepage-news-tone" required>
                  <Select
                    value={form.tone}
                    onValueChange={(tone) =>
                      setForm((current) => ({
                        ...current,
                        tone: tone as HomepageNewsTone,
                      }))
                    }
                  >
                    <SelectTrigger id="homepage-news-tone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Information">Information</SelectItem>
                      <SelectItem value="Important">Wichtig</SelectItem>
                      <SelectItem value="Urgent">Dringend</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              <FormField label="Titel" htmlFor="homepage-news-title" required>
                <Input
                  id="homepage-news-title"
                  maxLength={120}
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, title: event.target.value }))
                  }
                />
              </FormField>

              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-medium">Inhalt</h3>
                  <p className="text-sm text-muted-foreground">
                    Stellen Sie Absätze, Überschriften, Listen und Hervorhebungen
                    zusammen.
                  </p>
                </div>
                {form.blocks.map((block, index) => (
                  <BlockEditor
                    key={`${index}-${block.type}`}
                    block={block}
                    index={index}
                    count={form.blocks.length}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        blocks: current.blocks.map((item, itemIndex) =>
                          itemIndex === index ? value : item,
                        ),
                      }))
                    }
                    onMove={(direction) =>
                      setForm((current) => ({
                        ...current,
                        blocks: moveBlock(current.blocks, index, direction),
                      }))
                    }
                    onRemove={() =>
                      setForm((current) => ({
                        ...current,
                        blocks: current.blocks.filter(
                          (_, itemIndex) => itemIndex !== index,
                        ),
                      }))
                    }
                  />
                ))}
                <AddBlockButtons
                  onAdd={(type) =>
                    setForm((current) => ({
                      ...current,
                      blocks: [...current.blocks, newBlock(type)],
                    }))
                  }
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  label="Button-Beschriftung"
                  htmlFor="homepage-news-action-label"
                >
                  <Input
                    id="homepage-news-action-label"
                    maxLength={80}
                    placeholder="Zum Echt-System"
                    value={form.actionLabel}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        actionLabel: event.target.value,
                      }))
                    }
                  />
                </FormField>
                <FormField
                  label="Button-Ziel"
                  htmlFor="homepage-news-action-url"
                  description="Interner Pfad oder vollständige https-Adresse"
                >
                  <Input
                    id="homepage-news-action-url"
                    placeholder="https://berky.digital.de"
                    value={form.actionUrl}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        actionUrl: event.target.value,
                      }))
                    }
                  />
                </FormField>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField label="Gültig von" htmlFor="homepage-news-valid-from" required>
                  <Input
                    id="homepage-news-valid-from"
                    type="datetime-local"
                    value={form.validFrom}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        validFrom: event.target.value,
                      }))
                    }
                  />
                </FormField>
                <FormField
                  label="Gültig bis"
                  htmlFor="homepage-news-valid-until"
                  required
                >
                  <Input
                    id="homepage-news-valid-until"
                    type="datetime-local"
                    value={form.validUntil}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        validUntil: event.target.value,
                      }))
                    }
                  />
                </FormField>
              </div>

              {validation && (
                <p role="alert" className="text-sm font-medium text-destructive-strong">
                  {validation}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={Boolean(validation) || save.isPending}>
                  {editing ? 'Änderungen speichern' : 'News planen'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                >
                  <Eye aria-hidden="true" />
                  Als Popup öffnen
                </Button>
                {editing && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setEditing(null)
                      setForm(emptyForm())
                    }}
                  >
                    Bearbeitung abbrechen
                  </Button>
                )}
              </div>
            </form>

            <aside
              className="xl:sticky xl:top-4 xl:self-start"
              aria-label="Live-Vorschau"
            >
              <Card>
                <CardHeader>
                  <CardDescription>Live-Vorschau</CardDescription>
                  <CardTitle>{form.title.trim() || 'Titel Ihrer News'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <HomepageNewsContent news={previewDraft(form)} />
                </CardContent>
              </Card>
            </aside>
          </div>
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        rows={news.data ?? []}
        rowKey={(row) => row.id}
        caption="Geplante und bisherige Startseiten-News"
        error={news.isError ? { onRetry: () => news.refetch() } : null}
      />

      <HomepageNewsDialog
        news={previewDraft(form)}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        preview
      />
      <HomepageNewsStatisticsDialog
        news={statisticsNews}
        onOpenChange={(open) => !open && setStatisticsNews(null)}
      />
      <ConfirmDialog
        open={cancelCandidate !== null}
        onOpenChange={(open) => !open && setCancelCandidate(null)}
        title="Startseiten-News beenden?"
        description="Die News wird nicht mehr nach der Anmeldung angezeigt. Der Eintrag bleibt in der Übersicht erhalten."
        confirmLabel="News beenden"
        pending={cancel.isPending}
        destructive
        onConfirm={() => cancelCandidate && cancel.mutateAsync(cancelCandidate.id)}
      />
    </div>
  )
}

function BlockEditor({
  block,
  index,
  count,
  onChange,
  onMove,
  onRemove,
}: {
  block: HomepageNewsBlock
  index: number
  count: number
  onChange: (block: HomepageNewsBlock) => void
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
}) {
  const label = blockLabel(block.type)
  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{label}</span>
        <div className="ml-auto flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={`${label} nach oben`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUp aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={`${label} nach unten`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDown aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={`${label} entfernen`}
            disabled={count === 1}
            onClick={onRemove}
          >
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      </div>
      {block.type === 'bulletList' ? (
        <Textarea
          aria-label={`${label} bearbeiten`}
          value={block.items.join('\n')}
          placeholder="Ein Punkt pro Zeile"
          onChange={(event) =>
            onChange({ ...block, items: event.target.value.split('\n') })
          }
        />
      ) : (
        <Textarea
          aria-label={`${label} bearbeiten`}
          maxLength={1_000}
          value={block.text ?? ''}
          onChange={(event) => onChange({ ...block, text: event.target.value })}
        />
      )}
    </div>
  )
}

function AddBlockButtons({ onAdd }: { onAdd: (type: HomepageNewsBlockType) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(
        [
          ['paragraph', 'Absatz'],
          ['heading', 'Zwischenüberschrift'],
          ['bulletList', 'Aufzählung'],
          ['highlight', 'Hervorhebung'],
        ] as const
      ).map(([type, label]) => (
        <Button
          key={type}
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onAdd(type)}
        >
          <Plus aria-hidden="true" />
          {label}
        </Button>
      ))}
    </div>
  )
}

function NewsStatus({ news, now }: { news: HomepageNews; now: number }) {
  if (news.isCancelled) return <StatusBadge tone="neutral">Beendet</StatusBadge>
  if (Date.parse(news.validUntil) <= now)
    return <StatusBadge tone="neutral">Abgelaufen</StatusBadge>
  if (Date.parse(news.validFrom) > now)
    return <StatusBadge tone="info">Geplant</StatusBadge>
  return <StatusBadge tone="success">Aktiv</StatusBadge>
}

interface NewsForm {
  eyebrow: string
  title: string
  tone: HomepageNewsTone
  blocks: HomepageNewsBlock[]
  actionLabel: string
  actionUrl: string
  validFrom: string
  validUntil: string
}

function emptyForm(): NewsForm {
  const start = new Date()
  start.setSeconds(0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 7)
  return {
    eyebrow: 'Berky Digital',
    title: '',
    tone: 'Information',
    blocks: [{ ...EMPTY_BLOCK }],
    actionLabel: '',
    actionUrl: '',
    validFrom: toLocalInput(start),
    validUntil: toLocalInput(end),
  }
}

function fromNews(news: HomepageNews): NewsForm {
  return {
    eyebrow: news.eyebrow,
    title: news.title,
    tone: news.tone,
    blocks: news.blocks,
    actionLabel: news.actionLabel ?? '',
    actionUrl: news.actionUrl ?? '',
    validFrom: toLocalInput(new Date(news.validFrom)),
    validUntil: toLocalInput(new Date(news.validUntil)),
  }
}

function toDraft(form: NewsForm): HomepageNewsDraft {
  return {
    eyebrow: form.eyebrow.trim(),
    title: form.title.trim(),
    tone: form.tone,
    blocks: form.blocks.map((block) => ({
      ...block,
      text: block.text?.trim() || null,
      items: block.items.map((item) => item.trim()).filter(Boolean),
    })),
    actionLabel: form.actionLabel.trim() || null,
    actionUrl: form.actionUrl.trim() || null,
    validFrom: new Date(form.validFrom).toISOString(),
    validUntil: new Date(form.validUntil).toISOString(),
  }
}

function previewDraft(form: NewsForm): HomepageNewsDraft {
  const valid = form.blocks
    .map((block) => ({
      ...block,
      text: block.text?.trim() || null,
      items: block.items.map((item) => item.trim()).filter(Boolean),
    }))
    .filter((block) => block.text || block.items.length > 0)
  return {
    ...toDraft({
      ...form,
      title: form.title.trim() || 'Titel Ihrer News',
      blocks:
        valid.length > 0
          ? valid
          : [{ ...EMPTY_BLOCK, text: 'Ihr Inhalt erscheint hier.' }],
    }),
  }
}

function validate(form: NewsForm): string | null {
  if (!form.title.trim()) return 'Geben Sie einen Titel ein.'
  if (
    form.blocks.some((block) =>
      block.type === 'bulletList'
        ? block.items.every((item) => !item.trim())
        : !block.text?.trim(),
    )
  )
    return 'Füllen Sie alle Inhaltsblöcke aus.'
  if (!form.validFrom || !form.validUntil)
    return 'Geben Sie den vollständigen Zeitraum an.'
  if (Date.parse(form.validUntil) <= Date.parse(form.validFrom))
    return '„Gültig bis“ muss nach „Gültig von“ liegen.'
  if (Boolean(form.actionLabel.trim()) !== Boolean(form.actionUrl.trim()))
    return 'Button-Beschriftung und Button-Ziel gehören zusammen.'
  if (form.actionUrl.trim() && !isSafeUrl(form.actionUrl.trim()))
    return 'Das Button-Ziel muss ein interner Pfad oder eine https-Adresse sein.'
  return null
}

function isSafeUrl(value: string): boolean {
  if (value.startsWith('/') && !value.startsWith('//')) return true
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

function newBlock(type: HomepageNewsBlockType): HomepageNewsBlock {
  return type === 'bulletList'
    ? { type, text: null, items: [''] }
    : { type, text: '', items: [] }
}

function blockLabel(type: HomepageNewsBlockType): string {
  return {
    paragraph: 'Absatz',
    heading: 'Zwischenüberschrift',
    bulletList: 'Aufzählung',
    highlight: 'Hervorhebung',
  }[type]
}

function moveBlock(
  blocks: HomepageNewsBlock[],
  index: number,
  direction: -1 | 1,
): HomepageNewsBlock[] {
  const target = index + direction
  if (target < 0 || target >= blocks.length) return blocks
  const next = [...blocks]
  ;[next[index], next[target]] = [next[target]!, next[index]!]
  return next
}

function toLocalInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}
